// =============================================
// Entitlements — Client's Accounts access limits
// =============================================
// Central place for "who can do what" in the Client's Accounts service.
//
// Rule (set 2026-06, revised): the Client's Accounts pillar is SUBSCRIPTION-FIRST.
// With NO active subscription, an Account Owner / User cannot add any client and
// only sees the subscription page (the same family/business functionality is still
// available under Your Accounts). Once the owner buys the Client's Accounts package
// (an OWNER-LEVEL subscription, see below), client management unlocks.
//
// Owner-level subscription = a `user_client_subscription` row that is not tied to a
// specific client: `client_id = 0`, `client_type = 'owner_level'`. This is the
// "I run a bookkeeping business" purchase, distinct from the per-client package rows
// (which assign a package to one specific client).
//
// Platform/support actors (Site Owner, admins, support staff) are UNLIMITED —
// restrictions apply only to Account Owners and their Users.
//
// Future: franchisees pay a royalty + ad fund instead of a subscription, so they
// also resolve to "unlimited" here (see isFranchiseeActor stub below).
// =============================================
import { isAdminRole, UserRole } from '../types/user';
import { getTableData, addRow } from '../features/db-viewer/data/dataAccess';

// Owner-level subscription sentinel (a subscription not tied to one client).
export const OWNER_LEVEL_CLIENT_ID = 0;
export const OWNER_LEVEL_CLIENT_TYPE = 'owner_level';

// Subscribed owners are unlimited for now (per-package limits are future work).
// Kept for the doc/transaction caps that still apply to nothing on the free tier
// (free = no client access at all, so these are effectively 0 until subscribed).
export const CLIENT_FREE_LIMITS = {
  maxClients: 0,
  maxDocuments: 0,
  maxBkTransactions: 0,
};

function sessionRole(): UserRole | '' {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return '';
    return (JSON.parse(raw).role || '') as UserRole;
  } catch { return ''; }
}

/** Platform/support staff — no restrictions (Site Owner, admins, support). */
export function isPlatformActor(): boolean {
  const r = sessionRole();
  return !!r && isAdminRole(r as UserRole);
}

function sessionUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const id = JSON.parse(raw).id;
    return id != null ? Number(id) : null;
  } catch { return null; }
}

/**
 * Franchisees pay royalty + ad fund instead of a subscription → unlimited clients.
 * Franchisee is a CAPABILITY: either the dedicated 'franchisee' role OR any user
 * who owns an active user_franchisee row (an Account Owner who became a franchisee).
 */
export function isFranchiseeActor(): boolean {
  if (sessionRole() === 'franchisee') return true;
  const uid = sessionUserId();
  if (uid == null) return false;
  return getTableData('user_franchisee').some((f: any) =>
    // eslint-disable-next-line eqeqeq
    f.user_id == uid && f.is_deleted != 1 && f.is_active != 0);
}

function activeClientSubscriptions(accountOwnerId: number): number {
  return getTableData('user_client_subscription').filter((s: any) =>
    // eslint-disable-next-line eqeqeq
    s.account_owner_id == accountOwnerId && s.status === 'active' && s.is_deleted != 1
  ).length;
}

/** Does the owner hold an active OWNER-LEVEL Client's Accounts subscription? */
export function hasOwnerLevelSubscription(accountOwnerId: number): boolean {
  return getTableData('user_client_subscription').some((s: any) =>
    // eslint-disable-next-line eqeqeq
    s.account_owner_id == accountOwnerId &&
    s.client_type === OWNER_LEVEL_CLIENT_TYPE &&
    s.status === 'active' && s.is_deleted != 1
  );
}

/**
 * Is the Client's Accounts pillar unlocked for this owner?
 * Platform/support staff and franchisees never need a subscription; everyone else
 * must hold an active owner-level subscription.
 */
export function isClientAccountsSubscribed(accountOwnerId: number): boolean {
  if (isPlatformActor() || isFranchiseeActor()) return true;
  return hasOwnerLevelSubscription(accountOwnerId);
}

/**
 * Buy the Client's Accounts package (functional mock — no real payment).
 * Writes an owner-level subscription row. Idempotent: a no-op if already subscribed.
 */
export async function subscribeClientAccounts(
  accountOwnerId: number,
  packageId: number,
  billingCycle: 'monthly' | 'annual' = 'monthly',
): Promise<void> {
  if (hasOwnerLevelSubscription(accountOwnerId)) return;
  const today = new Date().toISOString().substring(0, 10);
  await addRow('user_client_subscription', {
    account_owner_id: accountOwnerId,
    client_id: OWNER_LEVEL_CLIENT_ID,
    client_type: OWNER_LEVEL_CLIENT_TYPE,
    package_id: packageId,
    billing_cycle: billingCycle,
    status: 'active',
    start_date: today,
    is_active: 1,
    is_deleted: 0,
    created_by: accountOwnerId,
  }, 'subscription_id');
}

export interface ClientEntitlement {
  unlimited: boolean;
  maxClients: number;        // Infinity when unlimited
  maxDocuments: number;
  maxBkTransactions: number;
  plan: 'platform' | 'franchisee' | 'subscribed' | 'free';
}

const UNLIMITED = (plan: ClientEntitlement['plan']): ClientEntitlement => ({
  unlimited: true, maxClients: Infinity, maxDocuments: Infinity, maxBkTransactions: Infinity, plan,
});

export function getClientEntitlement(accountOwnerId: number): ClientEntitlement {
  if (isPlatformActor()) return UNLIMITED('platform');
  if (isFranchiseeActor()) return UNLIMITED('franchisee');
  if (activeClientSubscriptions(accountOwnerId) > 0) return UNLIMITED('subscribed');
  return {
    unlimited: false,
    maxClients: CLIENT_FREE_LIMITS.maxClients,
    maxDocuments: CLIENT_FREE_LIMITS.maxDocuments,
    maxBkTransactions: CLIENT_FREE_LIMITS.maxBkTransactions,
    plan: 'free',
  };
}

export interface GateResult {
  allowed: boolean;
  reason: string;
  limit: number;
  current: number;
}

// ---- Counts ----
export function countPersonalClients(accountOwnerId: number): number {
  return getTableData('users').filter((u: any) =>
    // eslint-disable-next-line eqeqeq
    u.owner_id == accountOwnerId && u.relationship === 'Personal Client' && u.is_deleted != 1
  ).length;
}

export function countClientDocuments(accountOwnerId: number): number {
  return getTableData('dms_documents').filter((d: any) =>
    // eslint-disable-next-line eqeqeq
    d.account_owner_id == accountOwnerId &&
    (d.owner_type === 'client_personal' || d.owner_type === 'client_business') &&
    d.is_deleted != 1
  ).length;
}

// ---- Gates ----
export function canAddPersonalClient(accountOwnerId: number): GateResult {
  const ent = getClientEntitlement(accountOwnerId);
  const current = countPersonalClients(accountOwnerId);
  if (ent.unlimited || current < ent.maxClients) {
    return { allowed: true, reason: '', limit: ent.maxClients, current };
  }
  return {
    allowed: false,
    limit: ent.maxClients,
    current,
    reason: `Adding clients requires a Client's Accounts subscription. Subscribe to a package to start managing clients.`,
  };
}

export function canAddClientDocument(accountOwnerId: number, adding = 1): GateResult {
  const ent = getClientEntitlement(accountOwnerId);
  const current = countClientDocuments(accountOwnerId);
  if (ent.unlimited || current + adding <= ent.maxDocuments) {
    return { allowed: true, reason: '', limit: ent.maxDocuments, current };
  }
  return {
    allowed: false,
    limit: ent.maxDocuments,
    current,
    reason: `Your Free plan allows ${ent.maxDocuments} client documents. Subscribe to add more.`,
  };
}

// NOTE: a BK transactions/journal table does not exist yet, so the 200-transaction
// cap is defined above but cannot be counted/enforced until that table is built.
// Wire canAddClientTransaction() once acctg transactions are stored.
